/**
 * API Service
 * Handles all communication with the backend
 */

import { getIdToken } from '../config/firebase.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Make authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
    const token = await getIdToken();

    if (!token) {
        throw new Error('Not authenticated');
    }

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers
    });

    // Try to parse JSON, but fall back to text if the server returns HTML or other non-JSON responses
    let data = null;
    const text = await response.text();
    try {
        data = text ? JSON.parse(text) : null;
    } catch (e) {
        data = null; // Non-JSON response (e.g., HTML error page)
    }

    if (!response.ok) {
        const errMsg = (data && data.message) ? data.message : (text || `HTTP ${response.status} ${response.statusText}`);
        throw new Error(errMsg);
    }

    return data;
}

// ============================================================================
// LOCATION TRACKING SETTINGS API
// ============================================================================

/**
 * Get default settings template
 */
export async function getDefaultSettings() {
    const response = await fetch(`${API_BASE_URL}/api/location-tracking-settings/defaults`);
    return response.json();
}

/**
 * Get current user's settings
 */
export async function getMySettings() {
    return apiRequest('/api/location-tracking-settings');
}

/**
 * Update current user's settings
 */
export async function updateMySettings(settings) {
    return apiRequest('/api/location-tracking-settings', {
        method: 'POST',
        body: JSON.stringify(settings)
    });
}

/**
 * Enable a specific mode for current user
 */
export async function enableMode(mode, duration = null) {
    return apiRequest('/api/location-tracking-settings/enable-mode', {
        method: 'POST',
        body: JSON.stringify({ mode, duration })
    });
}

/**
 * Disable a specific mode for current user
 */
export async function disableMode(mode) {
    return apiRequest('/api/location-tracking-settings/disable-mode', {
        method: 'POST',
        body: JSON.stringify({ mode })
    });
}

/**
 * Reset current user's settings to defaults
 */
export async function resetMySettings(keepHistory = false) {
    return apiRequest('/api/location-tracking-settings/reset', {
        method: 'POST',
        body: JSON.stringify({ keepHistory })
    });
}

/**
 * Get settings change history
 */
export async function getSettingsHistory(limit = 20) {
    return apiRequest(`/api/location-tracking-settings/history?limit=${limit}`);
}

// ============================================================================
// ADMIN ENDPOINTS
// ============================================================================

/**
 * Admin: Get settings for a specific user
 */
export async function adminGetUserSettings(userId) {
    return apiRequest(`/api/location-tracking-settings/admin/user/${userId}`);
}

/**
 * Admin: Update settings for a specific user
 */
export async function adminUpdateUserSettings(userId, settings) {
    return apiRequest(`/api/location-tracking-settings/admin/user/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(settings)
    });
}

/**
 * Admin: Get all users' settings
 */
export async function adminGetAllSettings(limit = 50, startAfter = null) {
    let url = `/api/location-tracking-settings/admin/all?limit=${limit}`;
    if (startAfter) {
        url += `&startAfter=${startAfter}`;
    }
    return apiRequest(url);
}

/**
 * Admin: Broadcast settings to all users
 */
export async function adminBroadcastSettings(settings, reason = '') {
    return apiRequest('/api/location-tracking-settings/admin/broadcast', {
        method: 'POST',
        body: JSON.stringify({ settings, reason })
    });
}

/**
 * Admin: Force sync settings to a specific user
 * This triggers an immediate sync and saves all settings at once
 */
export async function adminForceSyncUserSettings(userId) {
    return apiRequest(`/api/location-tracking-settings/admin/user/${userId}/force-sync`, {
        method: 'POST'
    });
}

// ============================================================================
// HEALTH CHECK
// ============================================================================

/**
 * Check API health
 */
export async function checkHealth() {
    try {
        const response = await fetch(`${API_BASE_URL}/api/location-tracking-settings/health`);
        return response.json();
    } catch (error) {
        return { success: false, error: error.message };
    }
}

// ============================================================================
// USER PROFILE API
// ============================================================================

/**
 * Get user profile
 */
export async function getUserProfile(userId) {
    return apiRequest(`/api/profile/${userId}`);
}

// ============================================================================
// LOCATION DATA API
// ============================================================================

/**
 * Get user's location history
 */
export async function getUserLocations(userId, limit = 100) {
    return apiRequest(`/api/location/user/${userId}?limit=${limit}`);
}

/**
 * Get user's location history with date range
 */
export async function getUserLocationsByDateRange(userId, startDate, endDate, limit = 500, cursor = null) {
    const start = startDate instanceof Date ? startDate.toISOString() : startDate;
    const end = endDate instanceof Date ? endDate.toISOString() : endDate;

    const params = new URLSearchParams({
        startDate: start,
        endDate: end,
        limit: String(limit)
    });

    if (cursor) {
        if (cursor.timestamp != null) params.set('cursorTimestamp', String(cursor.timestamp));
        if (cursor.id) params.set('cursorId', String(cursor.id));
    }

    return apiRequest(`/api/location/user/${userId}/range?${params.toString()}`);
}

/**
 * Get latest location for a user
 */
export async function getUserLatestLocation(userId) {
    return apiRequest(`/api/location/user/${userId}/latest`);
}

/**
 * Get locations for multiple users (for admin dashboard)
 */
export async function getMultipleUsersLocations(userIds, limit = 50) {
    return apiRequest('/api/location/admin/multiple', {
        method: 'POST',
        body: JSON.stringify({ userIds, limit })
    });
}

/**
 * Get all recent locations (admin)
 */
export async function getAllRecentLocations(limit = 100, hoursAgo = 24) {
    return apiRequest(`/api/location/admin/recent?limit=${limit}&hoursAgo=${hoursAgo}`);
}

/**
 * Get location statistics for a user
 */
export async function getUserLocationStats(userId) {
    return apiRequest(`/api/location/user/${userId}/stats`);
}

// ============================================================================
// EMAIL API
// ============================================================================

/**
 * Send an admin email to a user
 */
export async function sendAdminEmail({ to, from, subject, text, templateType }) {
    return apiRequest('/api/email/admin-send', {
        method: 'POST',
        body: JSON.stringify({ to, from, subject, text, templateType })
    });
}

// ============================================================================
// FCM / NOTIFICATIONS API
// ============================================================================

/**
 * Get FCM status for a user
 */
export async function getFCMUserStatus(userId) {
    return apiRequest(`/api/fcm/admin/status/${userId}`);
}

/**
 * Send wake FCM to a user
 * @param {string} userId - User ID to wake
 * @param {string} reason - Reason for wake (default: 'admin_manual_wake')
 * @param {boolean} startCycle - Whether to start 12-hour cycle (true) or send single FCM (false)
 */
export async function adminWakeUser(userId, reason = 'admin_manual_wake', startCycle = false) {
    return apiRequest('/api/fcm/admin/wake-user', {
        method: 'POST',
        body: JSON.stringify({ userId, reason, startCycle })
    });
}

/**
 * Cancel active wake cycle for a user
 */
export async function cancelWakeCycle(userId) {
    return apiRequest('/api/fcm/admin/cancel-wake', {
        method: 'POST',
        body: JSON.stringify({ userId })
    });
}

/**
 * Get wake history for a user
 */
export async function getFCMWakeHistory(userId) {
    return apiRequest(`/api/fcm/admin/wake-history/${userId}`);
}

/**
 * Get list of unreachable users
 */
export async function getUnreachableUsers() {
    return apiRequest('/api/fcm/admin/unreachable');
}

// ============================================================================
// RECOVERY SYSTEM API (Escalating FCM Wake)
// ============================================================================

/**
 * Trigger recovery for a device (escalating FCM schedule)
 * Called automatically when admin page detects device offline for 7+ minutes
 * Schedule: immediate → +1min → +3min → every 30min × 12 → cycle restart
 * @param {string} userId - User ID to recover
 */
export async function triggerRecovery(userId) {
    return apiRequest(`/api/fcm/trigger-recovery/${userId}`, {
        method: 'POST'
    });
}

/**
 * Cancel recovery for a device
 * Called automatically when admin page receives new data from the device
 * @param {string} userId - User ID to cancel recovery for
 */
export async function cancelRecovery(userId) {
    return apiRequest(`/api/fcm/cancel-recovery/${userId}`, {
        method: 'POST'
    });
}

/**
 * Get recovery status for a device
 * @param {string} userId - User ID to check
 */
export async function getRecoveryStatus(userId) {
    return apiRequest(`/api/fcm/recovery-status/${userId}`);
}

/**
 * Get all active recoveries
 */
export async function getActiveRecoveries() {
    return apiRequest('/api/fcm/active-recoveries');
}

// ============================================================================
// GEOFENCE API
// ============================================================================

export async function getGeofences(targetUserId = null) {
    const url = targetUserId ? `/api/geofences?targetUserId=${encodeURIComponent(targetUserId)}` : '/api/geofences';
    return apiRequest(url);
}

export async function createGeofence(payload) {
    return apiRequest('/api/geofences', {
        method: 'POST',
        body: JSON.stringify(payload)
    });
}

export async function deleteGeofence(geofenceId) {
    return apiRequest(`/api/geofences/${geofenceId}`, {
        method: 'DELETE'
    });
}

export async function updateGeofence(geofenceId, payload) {
    return apiRequest(`/api/geofences/${geofenceId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
    });
}

export async function getGeofence(geofenceId) {
    return apiRequest(`/api/geofences/${geofenceId}`);
}

export async function checkGeofence(latitude, longitude) {
    return apiRequest('/api/geofences/check', {
        method: 'POST',
        body: JSON.stringify({ latitude, longitude })
    });
}

export default {
    getDefaultSettings,
    getMySettings,
    updateMySettings,
    enableMode,
    disableMode,
    resetMySettings,
    getSettingsHistory,
    adminGetUserSettings,
    adminUpdateUserSettings,
    adminGetAllSettings,
    adminBroadcastSettings,
    adminForceSyncUserSettings,
    checkHealth,
    getUserProfile,
    getUserLocations,
    getUserLocationsByDateRange,
    getUserLatestLocation,
    getMultipleUsersLocations,
    getAllRecentLocations,
    getUserLocationStats,
    sendAdminEmail,
    getFCMUserStatus,
    adminWakeUser,
    cancelWakeCycle,
    getFCMWakeHistory,
    getUnreachableUsers,
    triggerRecovery,
    cancelRecovery,
    getRecoveryStatus,
    getActiveRecoveries,
    getGeofences,
    getGeofence,
    createGeofence,
    updateGeofence,
    deleteGeofence,
    checkGeofence
};

